import React, { useState } from "react";
import { intl, havePermissions } from "dmsbasereact";
import { DmsModal, DmsButton, fetchData, getToken } from "dmsbasereact";
import { Permissions } from "../../constants/Permissions";
import {
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip,
  Paper,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { getBaseURL } from "../../build_config";
import { RETURN_REASON_KEYS } from "../../constants/ReturnReasons";
import { ReturnStatus } from "../../constants/ReturnStatus";
import { getTranslatedDocumentName } from "../../utils/documentTranslations";
import { formatCnpj } from "../../utils/formatters";
import { useQualityIssues } from "../../constants/QualityIssues";
import { useTreatmentDiscontinuation } from "../../constants/TreatmentDiscontinuationEnum";

// Helper to translate return reason
const getTranslatedReturnReason = (reasonName) => {
  const translations = {
    [RETURN_REASON_KEYS.QUALITY_ISSUE]: intl.get("QualityIssue").d("Quality Issue"),
    [RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION]: intl.get("TreatmentDiscontinuation").d("Treatment Discontinuation"),
    [RETURN_REASON_KEYS.ORDER_DIVERGENCE]: intl.get("OrderDivergence").d("Order Divergence"),
  };
  return translations[reasonName] || reasonName;
};

// Helper to translate return status using existing utility
const getTranslatedStatus = (status) => {
  const statusValue = typeof status === 'object' && status?.value !== undefined ? status.value : status;
  return {
    label: ReturnStatus.getText(statusValue),
    color: ReturnStatus.getColor(statusValue)
  };
};

// Helper to check file type from filename
const getFileType = (filename) => {
  if (!filename) return 'unknown';
  const ext = filename.split('.').pop()?.toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExtensions = ['pdf'];

  if (imageExtensions.includes(ext)) return 'image';
  if (pdfExtensions.includes(ext)) return 'pdf';
  return 'unknown';
};

// Helper to get icon based on file type
const getFileIcon = (filename) => {
  const fileType = getFileType(filename);
  if (fileType === 'image') return <ImageIcon />;
  if (fileType === 'pdf') return <PictureAsPdfIcon />;
  return <ArticleIcon />;
};

export const DmsReturnViewButton = ({ row }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [returnData, setReturnData] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewedFiles, setViewedFiles] = useState(new Set());
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  // Novos estados para dados relacionados
  const [dispensingData, setDispensingData] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [requestingOrganizationData, setRequestingOrganizationData] = useState(null);
  const [returnReasonData, setReturnReasonData] = useState(null);

  // Translations for reason details
  const qualityIssues = useQualityIssues();
  const treatmentDiscontinuation = useTreatmentDiscontinuation();

  // Helper to translate reason details based on reason type
  const getTranslatedReasonDetails = (reasonDetails, reasonName) => {
    if (!reasonDetails) return null;

    if (reasonName === RETURN_REASON_KEYS.QUALITY_ISSUE) {
      return qualityIssues[reasonDetails] || reasonDetails;
    }
    if (reasonName === RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION) {
      return treatmentDiscontinuation[reasonDetails] || reasonDetails;
    }
    return reasonDetails;
  };

  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const isDisabled = !row || !row.pk;

  const openModal = () => {
    setModalOpen(true);
    fetchReturnDetails();
  };

  // Função para buscar dados relacionados

  const fetchReturnDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Uma única requisição para todos os dados
      const response = await fetchData(`/return/${row.pk}/complete`, "GET", true);
      const data = response.data?.data || response.data;

      if (data) {
        setReturnData(data);
        setFiles(data.files || []);
        setDispensingData(data.dispensing || null);
        setBatchData(data.batch || null);
      } else {
        throw new Error('No return data found');
      }
    } catch (err) {
      console.error("Error fetching return details:", err);
      setError(intl.get("ErrorFetchingReturnDetails").d("Error fetching return details."));
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setReturnData(null);
    setFiles([]);
    setSelectedFile(null);
    setLoading(false);
    setError(null);
    setViewedFiles(new Set());
    // Clean up blob URL
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    setImageLoading(false);
    // Limpar dados relacionados
    setDispensingData(null);
    setBatchData(null);
    setRequestingOrganizationData(null);
    setReturnReasonData(null);
  };

  const handleFileSelect = async (file) => {
    setViewedFiles((prev) => new Set(prev).add(file.pk));
    setSelectedFile(file);

    // If it's an image, fetch with authorization and create blob URL
    const fileType = getFileType(file.original_filename);
    if (fileType === 'image') {
      setImageLoading(true);
      setImageUrl(null);
      try {
        const token = getToken();
        const response = await fetch(`${getBaseURL()}/file/${file.pk}`, {
          headers: {
            Authorization: `Bearer ${token.access_token}`
          }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setImageUrl(url);
        }
      } catch (err) {
        console.error("Error loading image:", err);
      } finally {
        setImageLoading(false);
      }
    }
  };

  const handleBackToDetails = () => {
    setSelectedFile(null);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
  };

  const renderReturnDetails = () => {
    if (!returnData) return null;

    const InfoItem = ({ label, value }) => (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || intl.get("NotProvided").d("Not provided")}
        </Typography>
      </Box>
    );

    const SectionTitle = ({ title, isFirst = false }) => (
      <Typography variant="subtitle1" sx={{ mb: 2, mt: isFirst ? 0 : 2, pb: 1, fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main' }}>
        {title}
      </Typography>
    );

    const reasonName = returnReasonData?.name || returnData.return_reason?.name;
    const translatedReason = getTranslatedReturnReason(reasonName);
    const statusInfo = getTranslatedStatus(returnData.status);

    // Format date for display
    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR');
      } catch {
        return dateStr;
      }
    };

    return (
      <Box sx={{ p: 2 }}>
        {/* Return Information Section */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <SectionTitle title={intl.get("ReturnInformation").d("Return Information")} isFirst={true} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label={intl.get("ReturnReason").d("Return Reason")}
                value={translatedReason}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label={intl.get("Status").d("Status")}
                value={
                  <Chip
                    label={statusInfo.label}
                    color={statusInfo.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                }
              />
            </Grid>
            {returnData.reason_details && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label={intl.get("ReasonDetails").d("Reason Details")}
                  value={getTranslatedReasonDetails(returnData.reason_details, reasonName)}
                />
              </Grid>
            )}
            {returnData.reason_details_text && (
              <Grid item xs={12}>
                <InfoItem
                  label={intl.get("reasonTextContainer").d("Reason Details")}
                  value={returnData.reason_details_text}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <InfoItem
                label={intl.get("CreatedOn").d("Created On")}
                value={formatDate(returnData.created_on)}
              />
            </Grid>
            {returnData.updated_on && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label={intl.get("LastUpdated").d("Last Updated")}
                  value={formatDate(returnData.updated_on)}
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Dispensing/Batch Information */}
        {(dispensingData || batchData) && (
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <SectionTitle title={intl.get("ProductInformation").d("Product Information")} isFirst={true} />
            <Grid container spacing={2}>
              {dispensingData && (
                <>
                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label={intl.get("Patient").d("Patient")}
                      value={returnData.patient_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label={intl.get("Prescription").d("Prescription")}
                      value={returnData.prescription_identifier}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <InfoItem
                      label={intl.get("Capsules").d("Capsules")}
                      value={dispensingData.capsule_count}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <InfoItem
                      label={intl.get("Boxes").d("Boxes")}
                      value={dispensingData.box_count}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label={intl.get("Batch").d("Batch")}
                      value={dispensingData.batch_number}
                    />
                  </Grid>
                </>
              )}
              {batchData && (
                <>
                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label={intl.get("BatchNumber").d("Batch Number")}
                      value={batchData.number}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label={intl.get("Part").d("Part")}
                      value={batchData.part?.name}
                    />
                  </Grid>
                  {havePermissions([Permissions.EUROFARMA_GESTAO_LENA]) && (
                    <Grid item xs={6} sm={3}>
                      <InfoItem
                        label={intl.get("BatchQuantity").d("Batch Quantity")}
                        value={batchData.quantity}
                      />
                    </Grid>
                  )}
                  <Grid item xs={6} sm={3}>
                    <InfoItem
                      label={intl.get("FabricationDate").d("Fabrication")}
                      value={formatDate(batchData.fabrication)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        )}

        {/* Return Details Section */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <SectionTitle title={intl.get("returnDetails").d("Return Details")} isFirst={true} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label={intl.get("RequestingOrganization").d("Requesting Organization")}
                value={requestingOrganizationData?.name || returnData.requesting_organization?.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label={intl.get("CNPJ").d("CNPJ")}
                value={formatCnpj(returnData.requesting_organization?.cnpj)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label={intl.get("QuantityOfBoxesToReturn").d("Quantity of Boxes to Return")}
                value={returnData.quantity}
              />
            </Grid>
            {returnData.organization_stock !== null && returnData.organization_stock !== undefined && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label={intl.get("OrganizationStock").d("Organization Stock")}
                  value={returnData.organization_stock}
                />
              </Grid>
            )}
            {returnData.total_dispensed !== null && returnData.total_dispensed !== undefined && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label={intl.get("TotalDispensed").d("Total Dispensed")}
                  value={returnData.total_dispensed}
                />
              </Grid>
            )}
            {returnData.invoice_number && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label={intl.get("InvoiceNumber").d("Invoice Number")}
                  value={returnData.invoice_number}
                />
              </Grid>
            )}
            {returnData.sap_tracking_code && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label={intl.get("SAPTrackingCode").d("SAP Tracking Code")}
                  value={returnData.sap_tracking_code}
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Documents Section */}
        {files.length > 0 && (
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <SectionTitle title={intl.get("Documents").d("Documents")} isFirst={true} />
            <List sx={{ width: "100%", p: 0 }}>
              {files.map((file) => (
                <ListItem key={file.pk} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleFileSelect(file)}
                    sx={{
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      "&:hover": {
                        bgcolor: "action.hover",
                        boxShadow: 1,
                      },
                      py: 1,
                      px: 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: "primary.main" }}>
                      {getFileIcon(file.original_filename)}
                    </ListItemIcon>
                    <ListItemText
                      primary={getTranslatedDocumentName(file.document_name) || file.original_filename}
                      secondary={file.original_filename}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: '0.9rem',
                      }}
                    />
                    {viewedFiles.has(file.pk) && (
                      <ListItemIcon sx={{ minWidth: "auto", ml: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                    )}
                    <Tooltip title={intl.get("ViewDocument").d("View Document")}>
                      <ListItemIcon sx={{ minWidth: "auto", ml: 1, color: "primary.main" }}>
                        <VisibilityIcon fontSize="small" />
                      </ListItemIcon>
                    </Tooltip>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    const centerStyles = {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      minHeight: "50vh",
    };

    if (loading)
      return (
        <Box sx={centerStyles}>
          <CircularProgress />
        </Box>
      );
    if (error)
      return (
        <Box sx={centerStyles}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    if (selectedFile) {
      const fileUrl = `${getBaseURL()}/file/${selectedFile.pk}`;
      const token = getToken();
      const fileType = getFileType(selectedFile.original_filename);

      if (fileType === 'image') {
        if (imageLoading) {
          return (
            <Box sx={centerStyles}>
              <CircularProgress />
            </Box>
          );
        }
        return (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            minHeight: '50vh',
            bgcolor: 'grey.100'
          }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={selectedFile.original_filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            ) : (
              <Typography color="error">
                {intl.get("ErrorLoadingImage").d("Error loading image")}
              </Typography>
            )}
          </Box>
        );
      }

      // Default: PDF viewer
      return (
        <Worker workerUrl="/pdf.worker.min.js">
          <Viewer
            fileUrl={fileUrl}
            httpHeaders={{ Authorization: `Bearer ${token.access_token}` }}
            withCredentials={true}
            plugins={[defaultLayoutPluginInstance]}
          />
        </Worker>
      );
    }
    return renderReturnDetails();
  };

  return (
    <>
      <Tooltip title={intl.get("ViewReturn").d("View Return")}>
        <span>
          <IconButton onClick={openModal} color="primary" disabled={isDisabled}>
            <VisibilityIcon />
          </IconButton>
        </span>
      </Tooltip>
      {isModalOpen && (
        <DmsModal
          isVisible={isModalOpen}
          onClose={closeModal}
          fullWidth
          maxWidth={selectedFile ? "lg" : "md"}
        >
          <DmsModal.Header onClose={closeModal}>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              {selectedFile && (
                <Tooltip title={intl.get("BackToDetails").d("Back to Details")}>
                  <IconButton
                    onClick={handleBackToDetails}
                    sx={{ mr: 1.5 }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: "bold" }}
              >
                {selectedFile
                  ? getTranslatedDocumentName(selectedFile.document_name) || selectedFile.original_filename
                  : intl.get("ReturnInformation").d("Return Information")}
              </Typography>
            </Box>
          </DmsModal.Header>
          <DmsModal.Body sx={{ p: 0 }}>
            {renderContent()}
          </DmsModal.Body>
          <DmsModal.Footer>
            {selectedFile && (
              <DmsButton
                onClick={handleBackToDetails}
                variant="outlined"
                text={intl.get("BackToDetails").d("Back to Details")}
              />
            )}
            <DmsButton
              onClick={closeModal}
              variant="outlined"
              text={intl.get("Close").d("Close")}
            />
          </DmsModal.Footer>
        </DmsModal>
      )}
    </>
  );
};
